// @ts-nocheck
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { Node } from 'react';
import { get } from 'lodash';
import {
  NotificationContainer,
  NotificationManager,
} from 'react-notifications';
import { useTranslation } from 'react-i18next';
import useAPI from 'hooks/useAPI';
import { useUser, useToken } from './app-context';
import axios from 'axios'
import dayjs from 'utils/day';

export const INVOICES_PAGINATION_LIMIT = 15;

const BillingContext = createContext<Context>({
  planSub: null,
  fetchPlanSub: null,
  patchPlanSub: null,
});

export const usePlanSubscription = () => {
  const { planSub } = useContext(BillingContext);
  return { planSub };
};

export const usePlanInfo = () => {
  const { fetchPlanSub, patchPlanSub } = useContext(BillingContext);
  return { fetchPlanSub, patchPlanSub };
};

export const BillingProvider = ({ children }: ProviderProps) => {
  const { t } = useTranslation();
  const [planSub, setPlanSub] = useState<PlanSubscription | {} | null>(null);

  const user: CompleteUser | null | void = useUser();
  const token = useToken();
  const API = useAPI();

  // console.log('user', user)
  const companyId = get(user, ['company', 'uuid']);

  const showErrorToaster = useCallback(
    (err: string, isCustom: boolean = false): void =>
      NotificationManager.error(
        // isCustom ? err : t(`validation:errors.billing_context.${err}`),
        t('validation:errors.error')
      ),
    [t]
  );


  const fetchLiveUpdateAccess = useCallback(async () => {
    if (!companyId || !token) return null;
    try {
      const { data } = await API.get('companies/live-update-user-enabled', {
        params: { _: Date.now() },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      if (typeof data?.liveUpdateUserEnabled === 'boolean') {
        setPlanSub(prev => {
          const base =
            prev && typeof prev === 'object' ? prev : ({} as Record<string, unknown>);
          return { ...base, liveUpdateUserEnabled: data.liveUpdateUserEnabled };
        });
        return data.liveUpdateUserEnabled;
      }
    } catch (err) {
      console.error('[fetchLiveUpdateAccess]', err);
    }
    return null;
  }, [API, companyId, token]);

  const fetchPlanSub = useCallback(async () => {
    if (!companyId || !token) return null;

    try {
      const [{ data: usage }, accessRes] = await Promise.all([
        API.get(`usage`, {
          params: { _: Date.now() },
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        }),
        API.get('companies/live-update-user-enabled', {
          params: { _: Date.now() },
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        }).catch(() => null),
      ]);
      var currentDate = dayjs.utc();

      // Assuming usage.usage.startDate and usage.usage.endDate are in UTC format
      var startDate = dayjs.utc(usage.usage.startDate);
      var endDate = dayjs.utc(usage.usage.endDate);

      const usageData = usage?.usage || {};
      const accessFlag = accessRes?.data?.liveUpdateUserEnabled;
      // Prefer dedicated Access endpoint; fall back to /usage.
      const nextPlanSub = {
        ...usageData,
        ...(typeof accessFlag === 'boolean'
          ? { liveUpdateUserEnabled: accessFlag }
          : {}),
      };

      // Check if the current date is between start and end date
      if (!currentDate.isBetween(startDate, endDate, null, '[]')) {
        console.log('Plan expired');
      }

      setPlanSub(nextPlanSub);
      return nextPlanSub;
    } catch (err) {
      console.error(err);
      if (err.response) {
        setPlanSub({});
      }
      showErrorToaster('plan_subscription', true);
      return null;
    }
  }, [API, setPlanSub, showErrorToaster, companyId, token]);

  const patchPlanSub = useCallback((partial: Record<string, unknown>) => {
    setPlanSub(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      return { ...prev, ...partial };
    });
  }, []);


  useEffect(() => {
    fetchPlanSub();
  }, [fetchPlanSub]);

  useEffect(() => {
    fetchLiveUpdateAccess();
  }, [fetchLiveUpdateAccess]);

  return (
    <BillingContext.Provider
      value={{
        planSub,
        fetchPlanSub,
        patchPlanSub,
      }}
    >
      {children}
      <NotificationContainer />
    </BillingContext.Provider>
  );
};

type ProviderProps = {
  children: Node;
};

type Context = {
  planSub: PlanSubscription | {} | null;
  fetchPlanSub: (() => Promise<Record<string, unknown> | null>) | null;
  patchPlanSub: ((partial: Record<string, unknown>) => void) | null;
};
